<script>
  import { onDestroy, onMount, tick } from 'svelte';
  import { useNavigate } from 'svelte-navigator';
  import noop from 'lodash/noop';
  import debounce from 'lodash/debounce';
  import isEqual from 'lodash/isEqual';

  import {
    Button,
    DataTable,
    InlineNotification,
    Link,
    OverflowMenu,
    OverflowMenuItem,
    Select,
    SelectItem,
    Tag,
    Toolbar,
    ToolbarBatchActions,
    ToolbarContent,
    TooltipDefinition,
  } from 'carbon-components-svelte';
  import CheckmarkIcon from 'carbon-icons-svelte/lib/Checkmark16';
  import CloseIcon from 'carbon-icons-svelte/lib/Close16';
  import CloudUploadIcon from 'carbon-icons-svelte/lib/CloudUpload16';
  import CompareIcon from 'carbon-icons-svelte/lib/Compare16';
  import GroupIcon from 'carbon-icons-svelte/lib/Events16';
  import SendIcon from 'carbon-icons-svelte/lib/SendAlt16';
  import ViewIcon from 'carbon-icons-svelte/lib/View16';

  import AddOrEditLicenseNoteModal from '../../components/licenses/AddOrEditLicenseNoteModal.svelte';
  import Badge from '../../components/Badge.svelte';
  import CompareLicenseModal from '../../components/licenses/CompareLicenseModal.svelte';
  import CustomFilterWithRouting from '../../components/CustomFilterWithRouting.svelte';
  import CustomLicenseFeaturesCell from '../../components/licenses/CustomLicenseFeaturesCell.svelte';
  import CustomLicenseUploadCell from '../../components/licenses/CustomLicenseUploadCell.svelte';
  import JsonPassiveModal from '../customer-builds/JsonPassiveModal.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import PreviewGeneratedLicense from '../../components/licenses/PreviewGeneratedLicense.svelte';
  import PreviewSentEmailModal from '../../components/licenses/PreviewSentEmailModal.svelte';
  import RelativeDate from '../../components/RelativeDate.svelte';
  import SendEmailModal from '../../components/licenses/SendEmailModal.svelte';
  import ToolbarSearchWithRouting from '../../components/ToolbarSearchWithRouting.svelte';
  import UploadLicenseModal from '../../components/licenses/UploadLicenseModal.svelte';

  import { getGeneratedLicensesGroups, getGeneratedLicenses } from '../../services';
  import { parseSortEvent } from '../../utils';

  import { SUPPORTED_RENEWAL_CYCLES } from '../../components/licenses/LicenseTemplateBasicForm.svelte';

  const LICENSE_FILENAME_SUFFIX = '.lic';
  const navigate = useNavigate();

  let generatedLicenses = [],
    generatedLicensesCount = 0,
    groups = [],
    selectedRowIds = [],
    backupSelections = [],
    resultNotification;

  let loading = true,
    runningTasks = 0,
    searchTextInitialized = false,
    paginationInitialized = false,
    templateIdInitialized = false;

  const criteria = {
    templateId: null,
    selectedGroup: '',
    searchText: '',
    currentPage: 1,
    pageSize: 25,
    orderBy: null,
    orderDirection: null,
  };
  let prevCriteria = {};

  let modals = {
    note: { open: false },
    preview: { open: false },
    compare: { open: false },
    emailPreview: { open: false },
    emailSend: { open: false },
    logs: { open: false },
    upload: { open: false },
  };

  async function fetchGroups() {
    runningTasks++;

    try {
      groups = await getGeneratedLicensesGroups();
    } catch (error) {
      console.error('[GeneratedLicensesList] Failed to get groups...', error);
      resultNotification = {
        hideCloseButton: false,
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to get groups! Please try again later.',
      };
    } finally {
      runningTasks--;
    }
  }

  async function fetchGeneratedLicenses({ templateId, selectedGroup, searchText, pageSize, currentPage, orderBy, orderDirection }) {
    runningTasks++;

    try {
      const { items: licenses, count: totalItems } = await getGeneratedLicenses({
        templateId: templateId || undefined,
        ownerId: selectedGroup || undefined,
        search: searchText || undefined,
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        orderBy: orderBy || undefined,
        orderDirection: orderDirection || undefined,
      });

      generatedLicenses = licenses;
      generatedLicensesCount = totalItems;
    } catch (error) {
      console.error('[GeneratedLicensesList] Failed to get generated licenses...', error);
      resultNotification = {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to get generated licenses! Please try again later.',
      };
    } finally {
      runningTasks--;
    }
  }

  const fetchGeneratedLicensesDebounced = debounce(fetchGeneratedLicenses, 300);

  function onSearchTextInit() {
    searchTextInitialized = true;
  }

  function onPaginationInit() {
    paginationInitialized = true;
  }

  function onTemplateIdInit() {
    templateIdInitialized = true;
  }

  function showTemplateIdNotification() {
    resultNotification = {
      kind: 'info',
      title: 'Info:',
      subtitle: `Showing generated licenses for template: ${criteria.templateId}`,
      onClose: () => {
        criteria.templateId = null;
        resultNotification = undefined;
      },
    };
  }

  function handleFilterSelection({ detail: selection }) {
    criteria.selectedGroup = selection;
  }

  const onSortChange = (event) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      criteria.orderBy = orderBy;
      criteria.orderDirection = orderDirection;
    });

  function goToEntity(groupId, licenseTemplateId) {
    return function go(event) {
      event.preventDefault();
      navigate(`/group/${groupId}?template=${licenseTemplateId}#licenses`);
    };
  }

  function updatePageData(modification) {
    const { action, data } = modification;

    if (!data) {
      return;
    }

    switch (action) {
      case 'email-sent':
        generatedLicenses = generatedLicenses.map((license) => (license.id !== data.licenseId ? license : { ...license, emails: [data] }));
        break;

      case 'upload':
      case 'update-license':
        generatedLicenses = generatedLicenses.map((license) => (license.id !== data.id ? license : data));
        break;

      default:
        console.warn(`[GeneratedLicenseList] Unknown action "${action}" for page data update`, data);
        break;
    }
  }

  function closeModal(name, modification = null) {
    if (modification) {
      updatePageData(modification);
    }

    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  function openModal(name, forwardData = {}) {
    return function handleClickForOpen() {
      modals = {
        ...modals,
        [name]: { open: true, ...forwardData },
      };
    };
  }

  function normalizeLog(data) {
    let normalizedData = data;

    try {
      normalizedData = JSON.parse(data);
      return normalizedData;
    } catch (error) {
      return normalizedData;
    }
  }

  function isTableFilterable(hasGroupSelected, items) {
    if (hasGroupSelected) {
      return {
        batchSelection: true,
        selectable: true,
      };
    }

    const owners = new Set(items.map(({ ownerId }) => ownerId));

    if (owners.size === 1) {
      return {
        batchSelection: true,
        selectable: true,
      };
    }

    return {
      batchSelection: false,
      selectable: false,
    };
  }

  async function lockSelection(selections) {
    if (selections.length > 2) {
      await tick();
      // eslint-disable-next-line svelte/infinite-reactive-loop
      selectedRowIds = [...backupSelections];
      return;
    }

    backupSelections = [...selections];
  }

  onMount(fetchGroups);

  onDestroy(() => fetchGeneratedLicensesDebounced.cancel());

  $: if (templateIdInitialized && searchTextInitialized && paginationInitialized && !isEqual(criteria, prevCriteria)) {
    const initialized = Object.keys(prevCriteria).length > 0;
    const searchChanged = prevCriteria.searchText !== criteria.searchText;
    const clearedSearch = prevCriteria.searchText && !criteria.searchText;

    if (criteria.templateId && prevCriteria.templateId !== criteria.templateId) {
      showTemplateIdNotification();
    }

    if (!initialized || !searchChanged) {
      fetchGeneratedLicenses(criteria);
    } else {
      if (clearedSearch) {
        loading = true; // keep showing the results table
      }
      if (criteria.currentPage !== 1) {
        criteria.currentPage = 1;
      }
      fetchGeneratedLicensesDebounced(criteria);
    }

    prevCriteria = { ...criteria };
  }

  $: loading = runningTasks > 0;
  $: showLicensesTable = loading || !!criteria.selectedGroup || !!criteria.searchText || !!generatedLicenses.length;

  $: if (!criteria.selectedGroup) {
    selectedRowIds = [];
  }
  $: filterable = isTableFilterable(!!criteria.selectedGroup, generatedLicenses);
  // eslint-disable-next-line svelte/infinite-reactive-loop
  $: lockSelection(selectedRowIds);
  $: selectedLicenses = generatedLicenses.filter(({ id }) => selectedRowIds.includes(id));
</script>

<LoadingSpinner {loading}>
  {#if resultNotification}
    <InlineNotification
      class="bx--inline-notification--fit-content"
      kind={resultNotification.kind}
      lowContrast
      title={resultNotification.title}
      subtitle={resultNotification.subtitle}
      on:close={resultNotification.onClose}
    />
  {/if}
  {#if showLicensesTable}
    <div class="table">
      <DataTable
        headers={[
          { key: 'licenseOwner.name', value: 'Group Name', sort: noop },
          { key: 'licenseTemplate.name', value: 'License Name', sort: noop },
          { key: 'filename', value: 'Filename', display: (filename) => `${filename}${LICENSE_FILENAME_SUFFIX}`, sort: noop },
          { key: 'cycle', value: 'Cycle', display: (cycle) => SUPPORTED_RENEWAL_CYCLES[cycle], sort: noop },
          { key: 'features', value: 'Features', sort: false },
          {
            key: 'expirationDate',
            value: 'Expiration Date',
            display: (date, row) => (row.isPermanent ? 'No expiration' : date ?? '—'),
            sort: noop,
          },
          { key: 'emails', value: 'Email', sort: false },
          { key: 'uploadDestinations', value: 'Upload', sort: false },
          { key: 'createdAt', value: 'Generated', sort: noop },
          { key: 'createdByDisplayName', value: 'Generated By', sort: false },
          { key: 'actions', empty: true, sort: false },
        ]}
        {...filterable}
        bind:selectedRowIds
        rows={generatedLicenses}
        sortable
        on:click:header={onSortChange}
      >
        <div class:action-cell={cell.key === 'actions'} slot="cell" let:cell let:row>
          {#if cell.key === 'actions'}
            <Badge active={!!row.note} value="!">
              <OverflowMenu aria-label="Open and close list of options" flipped portalMenu>
                <OverflowMenuItem text="View details" on:click={openModal('preview', { data: row })} />
                <OverflowMenuItem text="View logs" on:click={openModal('logs', { data: normalizeLog(row.extra) })} />
                <OverflowMenuItem text="Note" on:click={openModal('note', { data: row })}>
                  <Badge active={!!row.note} value="!" position="middle-right">Note</Badge>
                </OverflowMenuItem>
              </OverflowMenu>
            </Badge>
          {:else if cell.key === 'licenseOwner.name'}
            <div class="grouped flex-cell flex-column">
              <strong>{row.licenseOwner.name}</strong>
            </div>
          {:else if cell.key === 'licenseTemplate.name'}
            <div class="grouped flex-cell flex-column">
              <div>
                {#if row.isDemo}
                  <Tag type="blue" size="sm">DEMO</Tag>
                {/if}
                {#if row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt}
                  <TooltipDefinition tooltipText="Deleted entity" portalTooltip>{row.licenseTemplate.name}</TooltipDefinition>
                {:else}
                  <Link
                    href={`/group/${row.ownerId}?template=${row.templateId}#licenses`}
                    on:click={goToEntity(row.ownerId, row.templateId)}
                  >
                    <strong>{row.licenseTemplate.name}</strong>
                  </Link>
                {/if}
              </div>
            </div>
          {:else if cell.key === 'features'}
            <CustomLicenseFeaturesCell features={row.features} />
          {:else if cell.key === 'emails'}
            <div class="modal-cell">
              {#if row.emails.length}
                <div class="status sent">
                  <CheckmarkIcon />
                </div>
                <div>
                  <Link icon={ViewIcon} on:click={openModal('emailPreview', { data: row.emails[0] })}>View</Link>
                  <Link
                    disabled={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt}
                    icon={SendIcon}
                    on:click={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt ? noop : openModal('emailSend', { data: row })}
                    >Resend</Link
                  >
                </div>
              {:else}
                <div class="status pending">
                  <CloseIcon />
                </div>
                <Link
                  disabled={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt}
                  icon={SendIcon}
                  on:click={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt ? noop : openModal('emailSend', { data: row })}
                  >Send</Link
                >
              {/if}
            </div>
          {:else if cell.key === 'uploadDestinations'}
            <div class="modal-cell">
              {#if row.uploadDestinations.length}
                <div class="status sent">
                  <CheckmarkIcon />
                </div>
                <CustomLicenseUploadCell destinations={row.uploadDestinations} />
              {:else}
                <div class="status pending">
                  <CloseIcon />
                </div>
                <Link
                  disabled={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt || !row.licenseTemplate.uploadDestinations?.length}
                  icon={CloudUploadIcon}
                  on:click={row.licenseTemplate.deletedAt || row.licenseOwner.deletedAt || !row.licenseTemplate.uploadDestinations?.length
                    ? noop
                    : openModal('upload', { data: row })}
                >
                  Upload
                </Link>
              {/if}
            </div>
          {:else if cell.key === 'createdAt'}
            <RelativeDate date={cell.value} />
          {:else if cell.display}
            {cell.display(cell.value, row)}
          {:else}
            {cell.value}
          {/if}
        </div>
        <Toolbar>
          <ToolbarBatchActions>
            <Button icon={CompareIcon} on:click={openModal('compare')} disabled={selectedRowIds.length < 2}>Compare</Button>
          </ToolbarBatchActions>
          <ToolbarContent>
            <div class="table-filters">
              <GroupIcon />
              <Select inline class="filter-selector" size="sm" selected={criteria.selectedGroup} on:update={handleFilterSelection}>
                <SelectItem value="" text="All Groups" />
                {#each groups as group (group.id)}
                  <SelectItem value={group.id} text={group.name} />
                {/each}
              </Select>
            </div>
            <ToolbarSearchWithRouting
              persistent
              placeholder="Search licenses..."
              bind:searchText={criteria.searchText}
              on:toolbarSearchValueInit={onSearchTextInit}
            />
          </ToolbarContent>
        </Toolbar>
      </DataTable>
      <PaginationWithRouting
        bind:page={criteria.currentPage}
        bind:pageSize={criteria.pageSize}
        pageSizes={[10, 25, 50]}
        totalItems={generatedLicensesCount}
        on:init={onPaginationInit}
      />
    </div>
  {:else}
    <InlineNotification lowContrast hideCloseButton kind="info" title="Info:" subtitle="No generated licenses found" />
  {/if}
  {#if modals.emailPreview.open}
    <PreviewSentEmailModal open email={modals.emailPreview.data} on:close={() => closeModal('emailPreview')} />
  {/if}
  {#if modals.emailSend.open}
    <SendEmailModal
      open
      license={modals.emailSend.data}
      on:close={({ detail: data }) =>
        closeModal('emailSend', {
          action: 'email-sent',
          data,
        })}
    />
  {/if}
  {#if modals.upload.open}
    <UploadLicenseModal
      open
      license={modals.upload.data}
      on:close={({ detail: data }) =>
        closeModal('upload', {
          action: 'upload',
          data,
        })}
    />
  {/if}
  {#if modals.note.open}
    <AddOrEditLicenseNoteModal
      open
      license={modals.note.data}
      on:close={({ detail: data }) =>
        closeModal('note', {
          action: 'update-license',
          data,
        })}
    />
  {/if}
  {#if modals.compare.open}
    <CompareLicenseModal open selections={selectedLicenses} on:close={() => closeModal('compare')} />
  {/if}
  {#if modals.preview.open}
    <PreviewGeneratedLicense open license={modals.preview.data} on:close={() => closeModal('preview')} />
  {/if}
  {#if modals.logs.open}
    <JsonPassiveModal open name="License generation logs" data={modals.logs.data} on:close={() => closeModal('logs')} />
  {/if}

  <CustomFilterWithRouting name="template" bind:filter={criteria.templateId} on:filterValueInit={onTemplateIdInit} />
</LoadingSpinner>

<style>
  .table :global(.bx--data-table-container) {
    padding-top: 0;
  }

  .table :global(.bx--data-table thead .bx--table-column-checkbox .bx--checkbox--inline) {
    display: none;
  }

  .table :global(.bx--toolbar-content) {
    gap: 0.5rem;
  }

  .table :global(.bx--toolbar-content .bx--search) {
    flex: 1 1 auto;
  }

  .table-filters {
    align-items: center;
    display: flex;
    padding: 0 1rem;
  }

  .table-filters :global(.bx--select--inline .bx--select-input) {
    max-width: 150px;
  }

  @media (max-width: 42rem) {
    .table :global(.bx--toolbar-content) {
      flex-direction: column;
      gap: 0;
      height: 6rem;
    }

    .table-filters {
      flex: 1 1 100%;
    }
  }

  .flex-cell {
    display: flex;
  }

  .flex-column {
    flex-direction: column;
  }

  .grouped :global(em) {
    font-size: 0.75rem;
  }

  .modal-cell {
    display: flex;
    align-items: center;
  }

  .modal-cell :global(a) {
    cursor: pointer;
  }

  .status {
    display: flex;
    align-items: center;
    padding-right: 0.5rem;
    margin-right: 0.5rem;
    border-right: 1px solid var(--cds-ui-04);
  }

  .status.sent :global(svg) {
    fill: #24a148;
  }

  .status.pending :global(svg) {
    fill: var(--cds-danger);
  }

  .action-cell {
    width: 40px;
  }
</style>
